import axios from 'axios';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from 'src/lib/api/axios';
import {
  CanvasDiff,
  CanvasHubExport,
  CanvasResponse,
  CanvasVersion,
  CanvasSummary,
  CanvasVersionContent,
  CreatedCanvas,
  ResourcePermission,
  ResourcePermissionRole,
  SharesResponse,
  ToolCallDecision,
  ToolCallDecisionKind
} from './types';

/**
 * Send feedback for a message
 * @param message_id - The ID of the message
 * @param thumbType - The type of feedback (up or down)
 * @param list - The list of feedback comments
 * @returns The response from the API
 */
export const sendFeedback = async (message_id: string, thumbType: 'up' | 'down', list?: string[]) => {
  if (!list) {
    list = [];
  }

  return await apiPost(
    `chat/message/${message_id}/feedback`,
    {
      message: list?.join(','),
      rating: thumbType === 'up' ? 2 : 1
    },
    {
      meta: {
        globalError: false,
        context: 'feedback-submission'
      }
    }
  );
};

/**
 * Get similar questions for a message
 * @param message_id - The ID of the message
 * @returns The similar questions
 */
export const getRelatedQuestions = async (message_id: string) => {
  // This is a post because we are performing a semantic search on the backend
  // which will not be idempotent
  const response = await apiPost(
    `chat/messages/${message_id}/similar`,
    {},
    {
      meta: {
        globalError: false, // Handle locally
        context: 'related-questions-fetch'
      }
    }
  );
  return response.data;
};

export interface ChatUploadResponse {
  file_id: string;
  token_count: number;
  is_exact: boolean;
  filename: string;
  document_id?: string;
  /** Browser-renderable preview (JPEG rendition) when the original can't decode natively (HEIC/HEIF). */
  preview_url?: string;
}

/**
 * Register an already-processed hub document as a pending chat attachment.
 * The document must have had Document Intelligence run before calling this.
 */
export async function registerHubDocumentForChat(documentId: string): Promise<ChatUploadResponse> {
  const response = await apiPost<ChatUploadResponse>(
    'chat/chats/upload/from_document',
    { document_id: documentId },
    { meta: { globalError: false, context: 'chat-register-hub-document' } }
  );
  return response.data;
}

/**
 * Delete a pending chat upload from the server.
 * Fire-and-forget — errors are silently ignored.
 */
export async function deleteChatUpload(fileId: string) {
  try {
    await apiDelete(`chat/chats/uploads/${fileId}`, {
      meta: {
        globalError: false,
        context: 'delete-chat-upload'
      }
    });
  } catch {
    // Best-effort cleanup — ignore errors
  }
}

export interface GroupShareInfo {
  group_id: string;
  group_name: string;
  permission: ResourcePermissionRole;
}

export interface ChatSharesResponse {
  user_shares: ResourcePermission[];
  group_shares: GroupShareInfo[];
}

export async function getExistingShares(session_id: string): Promise<ChatSharesResponse> {
  const response = await apiGet<ChatSharesResponse>(`chat/chats/${session_id}/shares`, {
    meta: {
      globalError: false,
      context: 'get-existing-shares'
    }
  });
  return response.data;
}

export async function addPeopleToChat(
  session_id: string,
  message: string,
  people: string[],
  permission: ResourcePermissionRole,
  groupIds?: string[]
): Promise<ResourcePermission[]> {
  const response = await apiPost(
    `chat/chats/${session_id}/shares`,
    {
      emails: people.length > 0 ? people : undefined,
      permission: permission,
      message: message,
      group_ids: groupIds
    },
    {
      meta: {
        globalError: false,
        context: 'add-people-to-chat'
      }
    }
  );
  return response.data;
}

export async function deleteChatShare(session_id: string, permission_id: string) {
  await apiDelete(`chat/chats/${session_id}/shares/${permission_id}`, {
    meta: {
      globalError: false,
      context: 'delete-chat-share'
    }
  });
}

export async function deleteChatGroupShare(session_id: string, group_id: string) {
  await apiDelete(`chat/chats/${session_id}/group-shares/${group_id}`, {
    meta: {
      globalError: false,
      context: 'delete-chat-group-share'
    }
  });
}

export async function updateExistingShare(
  session_id: string,
  permission_id: string,
  permission: ResourcePermissionRole
) {
  await apiPatch(`chat/chats/${session_id}/shares/${permission_id}?role=${permission}`, {
    meta: {
      globalError: false,
      context: 'update-existing-share'
    }
  });
}

// ============================================================================
// Specialist Sharing API
// ============================================================================

/**
 * Get existing shares for a specialist
 * @param specialistId - The ID of the specialist
 * @returns The shares response containing user shares and tenant share
 */
export async function getSpecialistShares(specialistId: string): Promise<SharesResponse> {
  const response = await apiGet(`specialists/${specialistId}/shares`, {
    meta: {
      globalError: false,
      context: 'get-specialist-shares'
    }
  });
  return response.data;
}

/**
 * Add shares to a specialist (share with users or the tenant).
 * Public (all-users) sharing is not offered from the client.
 * @param specialistId - The ID of the specialist
 * @param options - The share options
 * @returns The shares response
 */
export async function addSpecialistShare(
  specialistId: string,
  options: {
    emails?: string[];
    group_ids?: string[];
    share_with_tenant?: boolean;
    permission: ResourcePermissionRole;
    message?: string;
  }
): Promise<SharesResponse> {
  const response = await apiPost(`specialists/${specialistId}/shares`, options, {
    meta: {
      globalError: false,
      context: 'add-specialist-share'
    }
  });
  return response.data;
}

/**
 * Get the maximum share level allowed for a specialist based on its document filters
 * @param specialistId - The ID of the specialist
 * @returns The max share level and restriction reason (if any)
 */
export async function getSpecialistMaxShareLevel(specialistId: string): Promise<{
  max_level: 'private' | 'individual' | 'tenant' | 'public';
  restriction_reason?: string;
  eligible_user_emails?: string[];
  eligible_group_ids?: string[];
}> {
  const response = await apiGet(`specialists/${specialistId}/max-share-level`, {
    meta: {
      globalError: false,
      context: 'get-specialist-max-share-level'
    }
  });
  return response.data;
}

/**
 * Delete a share from a specialist
 * @param specialistId - The ID of the specialist
 * @param permissionId - The ID of the permission to delete
 */
export async function deleteSpecialistShare(specialistId: string, permissionId: string) {
  await apiDelete(`specialists/${specialistId}/shares/${permissionId}`, {
    meta: {
      globalError: false,
      context: 'delete-specialist-share'
    }
  });
}

export async function deleteSpecialistGroupShare(specialistId: string, groupId: string) {
  await apiDelete(`specialists/${specialistId}/group-shares/${groupId}`, {
    meta: {
      globalError: false,
      context: 'delete-specialist-group-share'
    }
  });
}

/**
 * Update an existing share on a specialist
 * @param specialistId - The ID of the specialist
 * @param permissionId - The ID of the permission to update
 * @param permission - The new permission level
 */
export async function updateSpecialistShare(
  specialistId: string,
  permissionId: string,
  permission: ResourcePermissionRole
) {
  await apiPatch(`specialists/${specialistId}/shares/${permissionId}?role=${permission}`, {
    meta: {
      globalError: false,
      context: 'update-specialist-share'
    }
  });
}

/**
 * Get SAS link for a figure
 * @param document_id - The external ID of the document
 * @param figure_id - The ID of the figure
 * @returns The SAS link response with URL and expiry time
 */
export async function getFigureSasLink(document_id: string, figure_id: number) {
  try {
    const response = await apiGet(`chat/figures/${document_id}/${figure_id}`, {
      meta: {
        globalError: false, // Handle locally
        context: 'figure-sas-link'
      }
    });
    return response.data; // { url, expiry_time }
  } catch (error) {
    console.error('Failed to get figure SAS link:', error);
    throw error;
  }
}

// ============================================================================
// Memory API
// ============================================================================

/**
 * Delete a memory entry
 * @param externalId - The external ID of the memory entry
 */
export async function deleteMemoryEntry(externalId: string) {
  await apiDelete(`/chat/memory/entries/${externalId}`, {
    meta: {
      globalError: false,
      context: 'delete-memory-entry'
    }
  });
}

/**
 * Create a new memory entry
 * @param content - The content of the memory
 * @param category - The category of the memory
 * @param subject - The subject of the memory (optional)
 * @returns The created memory entry
 */
export async function createMemoryEntry(params: { content: string; category: string; subject?: string }) {
  const response = await apiPost('/chat/memory/entries', params, {
    meta: {
      globalError: false,
      context: 'create-memory-entry'
    }
  });
  return response.data;
}

/**
 * Update the chat topic for a session (PATCH). Only the owner may update.
 * @param sessionId - The external ID of the chat session
 * @param topic - The new topic to display
 */
export async function patchChatTopic(sessionId: string, topic: string) {
  await apiPatch(
    `chat/chats/${sessionId}/title`,
    { title: topic },
    {
      meta: {
        globalError: false,
        context: 'update-chat-topic'
      }
    }
  );
}

export async function patchChatSessionSetting(
  sessionId: string,
  settingKey: string,
  valueBool: boolean
): Promise<{ settings: Record<string, boolean> }> {
  const response = await apiPatch(
    `chat/chats/${sessionId}/settings/${settingKey}`,
    { value_bool: valueBool },
    {
      meta: {
        globalError: false,
        context: 'patch-chat-session-setting'
      }
    }
  );
  return response.data;
}

/**
 * Update an existing memory entry
 * @param externalId - The external ID of the memory entry
 * @param content - The new content of the memory
 */
export async function updateMemoryEntry(externalId: string, params: { content: string }) {
  const response = await apiPatch(`/chat/memory/entries/${externalId}`, params, {
    meta: {
      globalError: false,
      context: 'update-memory-entry'
    }
  });
  return response.data;
}

/**
 * Get memory consent status
 * @returns The consent status and message
 */
export async function getMemoryConsent() {
  const response = await apiGet<{ consent: boolean; message: string }>('chat/memory/consent', {
    meta: {
      globalError: false,
      context: 'get-memory-consent'
    }
  });
  return response.data;
}

/**
 * Set memory consent status
 * @param consent - Whether to enable or disable memory
 * @returns The updated consent status and message
 */
export async function setMemoryConsent(consent: boolean) {
  const response = await apiPost<{ consent: boolean; message: string }>(
    'chat/memory/consent',
    { consent },
    {
      meta: {
        globalError: false,
        context: 'set-memory-consent'
      }
    }
  );
  return response.data;
}

/**
 * Get all memory entries
 * @returns The list of memories and metadata
 */
export async function getMemoryEntries<T>() {
  const response = await apiGet<T>('chat/memory/entries', {
    meta: {
      globalError: false,
      context: 'get-memory-entries'
    }
  });
  return response.data;
}

/**
 * Get memory categories
 * @returns The list of available categories
 */
export async function getMemoryCategories<T>() {
  const response = await apiGet<T>('chat/memory/categories', {
    meta: {
      globalError: false,
      context: 'get-memory-categories'
    }
  });
  return response.data;
}

/**
 * Clear all memory entries
 */
export async function clearAllMemories() {
  await apiDelete('chat/memory/entries', {
    meta: {
      globalError: false,
      context: 'clear-all-memories'
    }
  });
}
/**
 * List every canvas in the session, oldest first
 */
export async function getCanvases(sessionId: string): Promise<CanvasSummary[]> {
  const response = await apiGet(`chat/chats/${sessionId}/editor`, {
    meta: {
      globalError: false,
      context: 'get-canvases'
    }
  });
  return response.data.canvases;
}

/**
 * Create a canvas from its first save, in `sessionId` or, without one, in a new chat session
 * named `newChatName` (left unnamed when null)
 */
export async function createCanvas(
  sessionId: string | undefined,
  title: string,
  content: string,
  imageManifest: Record<string, string> = {},
  newChatName: string | null = null
): Promise<CreatedCanvas> {
  const response = await apiPost<CreatedCanvas>(
    sessionId ? `chat/chats/${sessionId}/editor` : 'chat/chats/editor/new',
    sessionId
      ? { title, content, image_manifest: imageManifest }
      : { title, content, image_manifest: imageManifest, chat_title: newChatName },
    {
      meta: {
        globalError: false,
        context: 'create-canvas'
      }
    }
  );
  return response.data;
}

/**
 * Get a canvas by ID with content
 */
export async function getCanvas(sessionId: string, canvasId: string): Promise<CanvasResponse> {
  const response = await apiGet(`chat/chats/${sessionId}/editor/${canvasId}`, {
    meta: {
      globalError: false,
      context: 'get-canvas'
    }
  });
  return response.data;
}

/**
 * Save canvas content, creating a new version
 */
export async function saveCanvas(
  sessionId: string,
  canvasId: string,
  title: string,
  content: string,
  imageManifest: Record<string, string> = {}
): Promise<CanvasResponse | null> {
  const response = await apiPut(
    `chat/chats/${sessionId}/editor/${canvasId}`,
    { title, content, image_manifest: imageManifest },
    {
      meta: {
        globalError: false,
        context: 'save-canvas'
      }
    }
  );
  if (response.status === 204) {
    return null;
  }
  return response.data;
}

/**
 * Update canvas title only (metadata, no new version)
 */
export async function patchCanvasTitle(sessionId: string, canvasId: string, title: string) {
  await apiPatch(
    `chat/chats/${sessionId}/editor/${canvasId}/title`,
    { title },
    {
      meta: {
        globalError: false,
        context: 'update-canvas-title'
      }
    }
  );
}

/**
 * Get all versions of a canvas
 */
export async function getCanvasVersions(sessionId: string, canvasId: string): Promise<CanvasVersion[]> {
  const response = await apiGet(`chat/chats/${sessionId}/editor/${canvasId}/versions`, {
    meta: {
      globalError: false,
      context: 'get-canvas-versions'
    }
  });
  return response.data.versions;
}

export async function getCanvasHubExports(sessionId: string, canvasId: string): Promise<CanvasHubExport[]> {
  const response = await apiGet(`chat/chats/${sessionId}/editor/${canvasId}/hub-exports`, {
    meta: {
      globalError: false,
      context: 'get-canvas-hub-exports'
    }
  });
  return response.data.exports;
}

/**
 * Get a specific version's content for preview
 */
export async function getCanvasVersion(
  sessionId: string,
  canvasId: string,
  externalDocumentId: string
): Promise<CanvasResponse> {
  const response = await apiGet(`chat/chats/${sessionId}/editor/${canvasId}/versions/${externalDocumentId}`, {
    meta: {
      globalError: false,
      context: 'get-canvas-version'
    }
  });
  return response.data;
}

/**
 * Get a version's content addressed by its integer `version_id`.
 *
 * Distinct from getCanvasVersion, which addresses a version by document ULID: a
 * chat card knows only the version number, and resolving it client-side would
 * depend on the 100-entry version list.
 */
export async function getCanvasVersionByNumber(
  sessionId: string,
  canvasId: string,
  versionId: number
): Promise<CanvasVersionContent> {
  const response = await apiGet(`chat/chats/${sessionId}/editor/${canvasId}/versions/by-number/${versionId}`, {
    meta: {
      globalError: false,
      context: 'get-canvas-version-by-number'
    }
  });
  return response.data;
}

/**
 * Get the structured diff between two canvas versions.
 *
 * `fromVersionId` / `toVersionId` are integer `version_id`s (not ULIDs).
 * Opts out of the global error toast so the caller surfaces failures itself.
 */
export async function getCanvasDiff(
  sessionId: string,
  canvasId: string,
  fromVersionId: number,
  toVersionId: number
): Promise<CanvasDiff> {
  const response = await apiGet(
    `chat/chats/${sessionId}/editor/${canvasId}/diff?from=${fromVersionId}&to=${toVersionId}`,
    {
      meta: {
        globalError: false,
        context: 'get-canvas-diff'
      }
    }
  );
  return response.data;
}

/**
 * Restore a previous version of a canvas
 */
export async function restoreCanvasVersion(
  sessionId: string,
  canvasId: string,
  externalDocumentId: string
): Promise<CanvasResponse> {
  const response = await apiPost(
    `chat/chats/${sessionId}/editor/${canvasId}/versions/${externalDocumentId}/restore`,
    {},
    {
      meta: {
        globalError: false,
        context: 'restore-canvas-version'
      }
    }
  );
  return response.data;
}

/**
 * Convert a draft part to a canvas
 */
export async function convertDraftToCanvas(
  sessionId: string,
  messageId: string | number,
  title: string
): Promise<{ canvas_id: string; title: string }> {
  const response = await apiPost(
    `chat/chats/${sessionId}/editor/from-draft`,
    {
      message_id: Number(messageId),
      title: title
    },
    {
      meta: {
        globalError: false,
        context: 'convert-draft-to-canvas'
      }
    }
  );

  return response.data;
}

/**
 * Export markdown content as a DOCX file via the backend.
 */
export async function exportDocx(title: string, markdown: string): Promise<Blob> {
  const response = await apiPost('chat/chats/export/docx', { title, markdown }, { responseType: 'blob' });
  return response.data;
}

/**
 * Export a committed canvas version, with its images, as a DOCX file.
 *
 * The server resolves the version's stored markdown and embeds its images, so
 * no markdown is sent. Omitting `externalDocumentId` exports the canvas head.
 */
export async function exportCanvasDocx(canvasId: string, externalDocumentId?: string | null): Promise<Blob> {
  const response = await apiPost(
    `chat/canvas/${canvasId}/export/docx`,
    { external_document_id: externalDocumentId ?? null },
    { responseType: 'blob' }
  );
  return response.data;
}

export async function exportDocxToHub(
  destination: 'personal' | 'organisation' | 'group',
  groupId: string | undefined,
  canvasId: string,
  canvasVersionId: number
): Promise<{ document_id: string }> {
  const response = await apiPost<{ document_id: string }>(
    'chat/chats/export/docx-to-hub',
    {
      destination,
      group_id: destination === 'group' ? groupId : null,
      canvas_id: canvasId,
      canvas_version_id: canvasVersionId
    },
    { meta: { globalError: false, context: 'export-docx-to-hub' } }
  );
  return response.data;
}

export async function postToolCallDecision(
  sessionId: string,
  toolCallId: string,
  decision: ToolCallDecisionKind,
  result?: Record<string, any> | null,
  toolName?: string | null
): Promise<ToolCallDecision> {
  const response = await apiPost<ToolCallDecision>(
    `chat/chats/${sessionId}/tool-call-decisions`,
    { tool_call_id: toolCallId, tool_name: toolName ?? null, decision, result: result ?? null },
    { meta: { globalError: false, context: 'tool-call-decision' } }
  );
  return response.data;
}

export const getTip = async (): Promise<{ tip: string | null }> => {
  const response = await apiGet<{ tip: string | null }>('chat/chats/tip', {
    meta: { globalError: false, context: 'get-tip', background: true }
  });
  return response.data;
};

/** A canvas markdown document, opened in its chat. `version_id` is set only for an older version. */
export interface CanvasDocumentTarget {
  session_id: string;
  canvas_id: string;
  version_id: number | null;
}

/** `null` is an ordinary document. `'denied'` is canvas markdown the viewer cannot open. */
export type CanvasDocumentLookup = CanvasDocumentTarget | null | 'denied';

const canvasDocumentTargets = new Map<string, CanvasDocumentLookup>();
const canvasDocumentTargetRequests = new Map<string, Promise<CanvasDocumentLookup>>();
let canvasDocumentViewer = '';

/** Drop cached lookups when the signed-in user or tenant changes. */
export function bindCanvasDocumentViewer(viewerKey: string) {
  if (viewerKey === canvasDocumentViewer) return;
  canvasDocumentViewer = viewerKey;
  canvasDocumentTargets.clear();
  canvasDocumentTargetRequests.clear();
}

export function canvasViewerKey(
  user: { user_id?: number | null; external_id?: string; active_tenant_code?: string | null } | null
) {
  if (!user) return '';
  return `${user.user_id ?? user.external_id ?? ''}:${user.active_tenant_code ?? ''}`;
}

function canvasLookupStorageKey(documentId: string) {
  return `${canvasDocumentViewer}\n${documentId}`;
}

/** A historical version never moves. A live head and a denial do, so they are not kept. */
function rememberCanvasDocumentTarget(documentId: string, value: CanvasDocumentLookup) {
  if (value === null || (value !== 'denied' && value.version_id != null)) {
    canvasDocumentTargets.set(canvasLookupStorageKey(documentId), value);
  }
}

export function canvasDocumentTargetKey(documentId: string) {
  return ['canvas-document-target', canvasDocumentViewer, documentId] as const;
}

export function peekCanvasDocumentTarget(documentId: string): CanvasDocumentLookup | undefined {
  return canvasDocumentTargets.get(canvasLookupStorageKey(documentId));
}

export async function fetchCanvasDocumentTarget(documentId: string): Promise<CanvasDocumentLookup> {
  const key = canvasLookupStorageKey(documentId);
  const viewerAtStart = canvasDocumentViewer;
  const known = canvasDocumentTargets.get(key);
  if (known !== undefined) return known;
  const inflight = canvasDocumentTargetRequests.get(key);
  if (inflight) return inflight;

  const request = apiGet<CanvasDocumentTarget>(`chat/canvases/by-document/${documentId}`, {
    meta: { globalError: false, background: true, context: 'canvas-document-target' }
  })
    .then((response): CanvasDocumentLookup => response.data)
    .catch((error: unknown): CanvasDocumentLookup => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      if (axios.isAxiosError(error) && error.response?.status === 403) return 'denied';
      throw error;
    })
    .then((value) => {
      if (canvasDocumentViewer === viewerAtStart) rememberCanvasDocumentTarget(documentId, value);
      return value;
    })
    .finally(() => {
      if (canvasDocumentTargetRequests.get(key) === request) canvasDocumentTargetRequests.delete(key);
    });
  canvasDocumentTargetRequests.set(key, request);
  return request;
}
